import { useCalculusTFunction } from '@retail/calculus/i18n';
import { EditConditionDialog } from '../../ConditionActionsBase';
import { CreateItemLevelSalesPriceConditionForm } from '../CreateSalesPriceCondition/CreateItemLevelSalesPriceConditionForm';
import { useConditionActions } from '@retail/calculus/context';
import { SalesPriceConditionSchema } from '@retail/calculus/components';
import { useController, useFormContext } from 'react-hook-form';
import { PriceSuggestionPriority, PriceSuggestionType } from '@retail/products/types';
import { useCallback, useMemo } from 'react';
import { getISO_8601Date } from '@retail/utils';
import { MarkupCondition, PriceSuggestionCondition } from '@retail/calculus/types';

interface Props {
  open: boolean;
  itemName: string;
  supplierNumber: number;
  mgItemNumber: number;
  mgSupplierItemNumber: number;
  markupCondition?: MarkupCondition;
  priceSourceCondition?: PriceSuggestionCondition;
  onClose: () => void;
}

export function EditSalesPriceConditionFromCalculusCardDialog({
  open,
  itemName,
  supplierNumber,
  mgItemNumber,
  mgSupplierItemNumber,
  markupCondition,
  priceSourceCondition,
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const { createMultipleConditions, removeCondition } = useConditionActions();
  const { control, reset, handleSubmit } = useFormContext<SalesPriceConditionSchema>();
  const priceSourceController = useController({ name: 'priceSourcePriority', control });
  const markupCoverageDegreeController = useController({ name: 'markupCoverageDegree', control });

  const updatedPriceSuggestionPriority: PriceSuggestionType[] = useMemo(
    () => priceSourceController.field.value,
    [priceSourceController.field.value]
  );

  const submitSalesPriceConditions = useCallback(
    (data: SalesPriceConditionSchema) => {
      const priceSuggestion = {
        content: {
          priceSuggestion: {
            priorities: data.priceSourcePriority.includes('MARKUP')
              ? data.priceSourcePriority
              : [...(data.priceSourcePriority ?? []), 'MARKUP'],
          },
        },
        binding: { mgSupplierItem: { supplierItemId: mgSupplierItemNumber } },
        validFrom: getISO_8601Date(data.fromDate),
      };
      const markup =
        data.markupCoverageDegree != null
          ? {
              content: {
                markupCoverageDegree: { coverageDegree: data.markupCoverageDegree },
              },
              binding: { mgSupplierItem: { supplierItemId: mgSupplierItemNumber } },
              validFrom: getISO_8601Date(data.fromDate),
            }
          : undefined;
      createMultipleConditions(markup ? [priceSuggestion, markup] : [priceSuggestion]);

      if (
        data.markupCoverageDegree == null &&
        markupCondition &&
        markupCondition?.bindingType !== 'MG_SUPPLIER_ITEM'
      ) {
        removeCondition([markupCondition.id], 'deactivate');
      }

      reset();
      onClose();
    },
    [
      mgSupplierItemNumber,
      createMultipleConditions,
      markupCondition,
      reset,
      onClose,
      removeCondition,
    ]
  );

  const isHigherLevelPrio = priceSourceCondition?.bindingType !== 'MG_SUPPLIER_ITEM';

  const changeInPriceSuggestion = useMemo(() => {
    if (isHigherLevelPrio) {
      return updatedPriceSuggestionPriority.length !== 0;
    }
    const updatedPrio = updatedPriceSuggestionPriority.filter((prio) => prio !== 'MARKUP');
    const currentPrio =
      priceSourceCondition?.priceSuggestionPriorities
        ?.filter((prio) => prio.priceSuggestionType !== 'MARKUP')
        .map((prio) => prio.priceSuggestionType) ?? [];

    return (
      updatedPrio?.length !== currentPrio?.length ||
      !updatedPrio.every((val, index) => val === currentPrio[index])
    );
  }, [
    isHigherLevelPrio,
    priceSourceCondition?.priceSuggestionPriorities,
    updatedPriceSuggestionPriority,
  ]);

  const changeInMarkupCoverageDegree = useMemo(
    () =>
      isHigherLevelPrio
        ? !!markupCoverageDegreeController.field.value
        : Number(markupCoverageDegreeController.field.value) !== markupCondition?.coverageDegree,
    [isHigherLevelPrio, markupCondition?.coverageDegree, markupCoverageDegreeController.field.value]
  );
  return (
    <EditConditionDialog
      open={open}
      title={t('calculus.salesPriceCondition.editSalesPriceCondition', { levelName: itemName })}
      dialogContent={
        <CreateItemLevelSalesPriceConditionForm
          mgSupplierId={supplierNumber}
          mgItemNumber={mgItemNumber}
          includeMarkup={true}
          markupCondition={
            markupCondition
              ? {
                  coverageDegree: markupCondition.coverageDegree!,
                  salesFactor: markupCondition.salesFactor!,
                  isInherited: markupCondition.bindingType !== 'MG_SUPPLIER_ITEM',
                  bindingInfo: {
                    bindingType: markupCondition.bindingType,
                    bindingTargetId: markupCondition.bindingTargetId,
                    bindingTargetName: markupCondition.bindingTargetName,
                  },
                }
              : undefined
          }
          isLoadingFetchPriceSuggestions={false}
        />
      }
      isDisabled={!changeInPriceSuggestion && !changeInMarkupCoverageDegree}
      onSubmit={handleSubmit(submitSalesPriceConditions)}
      onClose={onClose}
    />
  );
}
